// src/modules/kinema/fonts.ts
import { FontService } from '@services/font/FontService';
import type { FontFamily } from '@services/font/commands/cmdGetFontFamilies';
import type { EditorState } from '@/lib/kinema';

const fontService = new FontService();
const loadedFonts = new Set<string>();
let familiesCache: FontFamily[] | null = null;

function fontKey(family: string, weight: number, italic: boolean): string {
  return `${family}-${weight}${italic ? 'i' : ''}`;
}

function styleId(family: string, weight: number, italic: boolean): string {
  return `font-css-${family.replace(/\s+/g, '-')}-${weight}${italic ? 'i' : ''}`;
}

/**
 * Load a single font variant. Fetches CSS from the API, injects it into the DOM,
 * and waits for the browser to confirm the font is ready.
 */
export async function loadFont(family: string, weight = 400, italic = false): Promise<void> {
  const key = fontKey(family, weight, italic);
  if (loadedFonts.has(key)) return;

  // Check if CSS is already in the DOM (from a previous session or SSR)
  const id = styleId(family, weight, italic);
  if (!document.getElementById(id)) {
    try {
      const css = await fontService.getFontCss(family, weight, italic);
      const style = document.createElement('style');
      style.id = id;
      style.textContent = css;
      document.head.appendChild(style);
    } catch (e) {
      console.warn(`Failed to load font CSS for ${family} ${weight}:`, e);
      return;
    }
  }

  // Wait for the browser to actually load the font file. fonts.ready only waits on faces
  // already loading, and a face nothing has drawn with yet hasn't started (MPN-2033), so ask
  // for this one.
  try {
    await document.fonts.load(`${italic ? 'italic ' : ''}${weight} 16px "${family}"`);
  } catch {
    // A face that fails to load falls back like any other; the text still draws
  }

  loadedFonts.add(key);
}

/**
 * Scan editor state for all text layers and load their fonts.
 * Call this before first render to prevent fallback font flash.
 */
export async function loadFontsFromState(state: EditorState): Promise<void> {
  const seen = new Set<string>();
  const promises: Promise<void>[] = [];

  for (const layer of Object.values(state.layers)) {
    if (layer.type !== 'text') continue;
    const family = layer.fontFamily as string;
    const weight = (layer.fontWeight as number) || 400;
    const italic = layer.fontStyle === 'italic';
    const key = fontKey(family, weight, italic);
    if (seen.has(key)) continue;
    seen.add(key);
    promises.push(loadFont(family, weight, italic));
  }

  await Promise.all(promises);
}

/**
 * Fetch the list of available font families. Caches the result.
 */
export async function fetchFontFamilies(search?: string): Promise<FontFamily[]> {
  // If searching, always hit the API (search is server-side)
  if (search) {
    return fontService.getFontFamilies('google', search);
  }
  // Otherwise return cached full list
  if (familiesCache) return familiesCache;
  familiesCache = await fontService.getFontFamilies('google');
  return familiesCache;
}

/**
 * Look up a FontFamily by name from the cached list.
 * Returns undefined if the list hasn't been fetched yet or the font isn't found.
 */
export function getFontFamily(name: string): FontFamily | undefined {
  return familiesCache?.find((f) => f.family_name === name);
}
